import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import type { ReactNode } from "react";

export type HomeSection = "tracking" | "data-insight" | "planning";

export const HOME_SECTIONS: { value: HomeSection; label: string }[] = [
  { value: "planning", label: "Planning" },
  { value: "data-insight", label: "Data Insight" },
  { value: "tracking", label: "Tracking" },
];

const getTabId = (section: HomeSection) => `home-section-tab-${section}`;
const getPanelId = (section: HomeSection) => `home-section-panel-${section}`;

interface HomeSectionTabsProps {
  value: HomeSection;
  onChange: (section: HomeSection) => void;
}

export function HomeSectionTabs({ value, onChange }: HomeSectionTabsProps) {
  return (
    <Tabs
      aria-label="Personal update sections"
      value={value}
      onChange={(_, section: HomeSection) => onChange(section)}
      variant="scrollable"
      scrollButtons={false}
      sx={{
        minHeight: 40,
        mt: 1.5,
        fontFamily: '"Nunito", sans-serif',
        // Baseline under the tabs; the selected tab paints over it so it
        // reads as attached to the panel below.
        boxShadow: "inset 0 -1px 0 #DEE3EC",
        "& .MuiTabs-indicator": { display: "none" },
        "& .MuiTabs-flexContainer": { gap: 0.5 },
        "& .MuiTab-root": {
          flex: "0 1 280px",
          minWidth: 120,
          maxWidth: "none",
          minHeight: 40,
          px: 2,
          py: 0.75,
          border: "1px solid #DEE3EC",
          backgroundColor: "#F7F8FC",
          color: "#4F5D73",
          fontFamily: "inherit",
          fontSize: 14,
          fontWeight: 800,
          textTransform: "none",
          transition: "background-color 150ms ease, color 150ms ease",
          "&:hover": {
            backgroundColor: "#ECEFF5",
            color: "#19345D",
          },
          "&.Mui-focusVisible": {
            outline: "3px solid rgba(63, 80, 181, 0.22)",
            outlineOffset: -3,
          },
        },
        "& .MuiTab-root.Mui-selected": {
          backgroundColor: "#FFFFFF",
          borderBottomColor: "#FFFFFF",
          boxShadow: "inset 0 3px 0 #28A88A",
          color: "#19345D",
        },
      }}
    >
      {HOME_SECTIONS.map((section) => (
        <Tab
          key={section.value}
          label={section.label}
          value={section.value}
          id={getTabId(section.value)}
          aria-controls={getPanelId(section.value)}
        />
      ))}
    </Tabs>
  );
}

interface HomeSectionPanelProps {
  section: HomeSection;
  activeSection: HomeSection;
  children: ReactNode;
}

export function HomeSectionPanel({
  section,
  activeSection,
  children,
}: HomeSectionPanelProps) {
  const isActive = section === activeSection;

  return (
    <Box
      role="tabpanel"
      id={getPanelId(section)}
      aria-labelledby={getTabId(section)}
      hidden={!isActive}
    >
      {isActive && children}
    </Box>
  );
}

interface HomeSectionPlaceholderProps {
  icon: ReactNode;
  title: string;
  description: string;
}

export function HomeSectionPlaceholder({
  icon,
  title,
  description,
}: HomeSectionPlaceholderProps) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      gap={1}
      sx={{
        mt: 2.5,
        px: 3,
        py: 6,
        border: "1px dashed #DEE3EC",
        borderRadius: "7px",
        backgroundColor: "#F8F9FB",
        fontFamily: '"Nunito", sans-serif',
        "& .MuiTypography-root": { fontFamily: "inherit" },
        "& .MuiSvgIcon-root": { fontSize: 40, color: "#A0A9B8" },
      }}
    >
      {icon}
      <Typography
        component="h2"
        sx={{ m: 0, color: "#19345D", fontSize: 16, fontWeight: 800 }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ maxWidth: 420, color: "#667085", fontSize: 14, lineHeight: 1.5 }}
      >
        {description}
      </Typography>
    </Stack>
  );
}
